/**
 * /policies/payroll/calendars — Session 3c PR 16.
 * Owning agent: Rawatib.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { PayrollCalendarItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function PayrollCalendarsPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-payroll-calendars", le],
    queryFn: () => session3cApi.listPayrollCalendars(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<PayrollCalendarItem>[] = [
    {
      headerKey: "policies.payroll_calendars.col.type",
      label: "Period type",
      render: (r) => <span className="text-xs">{r.period_type}</span>,
    },
    {
      headerKey: "policies.payroll_calendars.col.cutoff",
      label: "Cutoff",
      render: (r) => <span className="font-mono text-xs">{r.cutoff_date}</span>,
    },
    {
      headerKey: "policies.payroll_calendars.col.pay",
      label: "Pay date",
      render: (r) => <span className="font-mono text-xs">{r.pay_date}</span>,
    },
    {
      headerKey: "policies.payroll_calendars.col.lock",
      label: "Lock lead time (days)",
      render: (r) => <span className="font-mono text-xs">{r.lock_lead_time_days}</span>,
    },
  ];

  return (
    <PolicyListPage<PayrollCalendarItem>
      title={t("policies.payroll_calendars.title", "Payroll Calendars")}
      subtitle={t("policies.payroll_calendars.subtitle", "Per-LE payroll period setup — cutoff dates · pay dates · period lock cadence.")}
      agent="rawatib"
      domain="payroll_calendars"
      newButtonLabel={t("policies.payroll_calendars.cta.new", "New Calendar Period")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.payroll_calendars.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.payroll_calendars.suggestion.clean_body", "Cutoff + pay date drive when /operations/payroll/period-lock surfaces the POST PERIOD action."),
          primaryActionLabel: t("policies.payroll_calendars.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "payroll-calendars-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.payroll_calendars.title", "Payroll Calendars"),
        window_label: t("policies.payroll_calendars.window", "active periods"),
      }}
    />
  );
}
